import React, { useState, useEffect, useRef } from 'react';
import ConceptInput from './components/ConceptInput';
import ToneSelector from './components/ToneSelector';
import ResultDisplay from './components/ResultDisplay';
import LoadingSpinner from './components/LoadingSpinner';
import SearchHistory from './components/SearchHistory';
import AboutModal from './components/AboutModal';
import { generateWords } from './api/generate';

function App() {
  const [concepts, setConcepts] = useState([]);
  const [context, setContext] = useState('');
  const [tones, setTones] = useState([]);
  const [outputTypes, setOutputTypes] = useState([]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAboutModal, setShowAboutModal] = useState(false);
  
  const [searchHistory, setSearchHistory] = useState(() => {
    const saved = localStorage.getItem('searchHistory');
    return saved ? JSON.parse(saved) : [];
  });

  const saveToHistory = (searchData, results) => {
    const historyItem = {
      ...searchData,
      results,
      timestamp: Date.now()
    };
    
    const newHistory = [historyItem, ...searchHistory.slice(0, 49)]; // Keep max 50 items
    setSearchHistory(newHistory);
    localStorage.setItem('searchHistory', JSON.stringify(newHistory));
  };

  const selectHistoryItem = (historyItem) => {
    setConcepts(historyItem.concepts);
    setContext(historyItem.context || '');
    setTones(historyItem.tones || []);
    setOutputTypes(historyItem.outputTypes || []);
    setResults(historyItem.results);
    setError('');
    
    // Scroll to results section after a short delay to ensure DOM updates
    setTimeout(() => {
      if (resultsRef.current) {
        resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const clearHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem('searchHistory');
  };

  const resultsRef = useRef(null);

  useEffect(() => {
    if (loading && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loading]);

  const handleGenerate = async () => {
    if (concepts.length === 0) {
      setError('Please enter at least 1 concept');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const searchData = {
        concepts,
        context,
        tones,
        outputTypes
      };
      
      const response = await generateWords(searchData);
      setResults(response);
      saveToHistory(searchData, response);
    } catch (err) {
      setError(err.message || 'Failed to generate words');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-4 sm:py-8 max-w-4xl">
        {/* Header */}
        <header className="text-center mb-6 sm:mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <h1 className="text-2xl sm:text-4xl font-bold text-gray-900">
              WordBetween
            </h1>
            <button
              onClick={() => setShowAboutModal(true)}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              title="About WordBetween"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </div>
          <p className="text-base sm:text-xl text-gray-600">
            Find the word between your ideas.
          </p>
        </header>

        {/* Main Form */}
        <main className="bg-white rounded-xl shadow-lg p-4 sm:p-8 mb-8">
          {/* Concept Input */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Concept Words
            </h2>
            <ConceptInput
              concepts={concepts}
              onChange={setConcepts}
            />
          </div>

          {/* Context Input */}
          <div className="mb-6 sm:mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Context (Optional)
            </h2>
            <textarea
              className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={3}
              placeholder="What are you naming or describing?"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              style={{ fontSize: '16px' }}
            />
          </div>

          {/* Tone and Output Type Selectors */}
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Tone
              </h2>
              <ToneSelector
                selected={tones}
                onChange={setTones}
                options={[
                  { value: 'clear', label: 'Clear' },
                  { value: 'playful', label: 'Playful' },
                  { value: 'professional', label: 'Professional' },
                  { value: 'technical', label: 'Technical' },
                  { value: 'elegant', label: 'Elegant' },
                  { value: 'weird', label: 'Weird' },
                  { value: 'brandable', label: 'Brandable' },
                  { value: 'minimal', label: 'Minimal' }
                ]}
              />
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Output Types
              </h2>
              <ToneSelector
                selected={outputTypes}
                onChange={setOutputTypes}
                options={[
                  { value: 'synonyms', label: 'Synonyms' },
                  { value: 'product-names', label: 'Product Names' },
                  { value: 'domain-names', label: 'Domain Names' },
                  { value: 'app-names', label: 'App Names' },
                  { value: 'technical-terms', label: 'Technical Terms' },
                  { value: 'poetic-words', label: 'Poetic Words' },
                  { value: 'rare-words', label: 'Rare Words' },
                  { value: 'alliteration', label: 'Alliteration' }
                ]}
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}

          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={loading || concepts.length === 0}
            className="w-full bg-blue-600 text-white py-4 px-6 text-lg font-semibold rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 min-h-[56px]"
            style={{ touchAction: 'manipulation' }}
          >
            {loading ? (
              <>
                <LoadingSpinner size="small" text="" />
                Finding Words...
              </>
            ) : (
              'Find Words'
            )}
          </button>
        </main>

        {/* Results */}
        <div ref={resultsRef}>
          {loading && (
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="large" text="Finding words between your concepts..." />
            </div>
          </div>
        )}
          {results && (
            <ResultDisplay results={results} />
          )}
        </div>

        {/* Search History */}
        <SearchHistory
          history={searchHistory}
          onSelectHistory={selectHistoryItem}
          onClearHistory={clearHistory}
        />
      </div>

        {/* About Modal */}
        <AboutModal 
          isOpen={showAboutModal} 
          onClose={() => setShowAboutModal(false)} 
        />
    </div>
  );
}

export default App;
